import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../../lib/utils";

const buttonVariants = cva(
  "inline-flex items-center rounded-lg  justify-center whitespace-nowrap  text-sm font-medium ring-offset-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-950 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 dark:ring-offset-stone-950 dark:focus-visible:ring-stone-300",
  {
    variants: {
      variant: {
        default:
          "bg-stone-900 text-stone-50 hover:bg-stone-900/90 dark:bg-accent dark:text-accent-foreground dark:hover:bg-accent/90",
        destructive:
          "bg-red-500 text-stone-50 hover:bg-red-500/90 dark:bg-red-900 dark:text-stone-50 dark:hover:bg-red-900/90",
        outline:
          "border border-stone-200 bg-white hover:bg-stone-100 hover:text-stone-900 dark:border-stone-800 dark:bg-stone-950 dark:hover:bg-stone-950/80 dark:hover:text-stone-50",
        secondary:
          "dark:bg-lime-200 text-lime-200 hover:bg-stone-800/80 bg-stone-950 dark:text-stone-950 dark:hover:bg-lime-200/80",
        downloadOutline:
          "border border-download-accent bg-transparent text-download-accent hover:bg-download-accent/10 focus-visible:ring-download-accent dark:focus-visible:ring-download-accent",
        secondaryOutline:
          "border dark:border-lime-200 text-lime-950 hover:text-stone-800 dark:text-lime-200 dark:hover:text-lime-200",
        ghost:
          "hover:bg-stone-100 hover:text-stone-900 dark:hover:bg-black/20 dark:hover:text-stone-50",
        portalGhost: "bg-transparent! hover:bg-background/20!",
        link: "text-stone-900 underline-offset-4 hover:underline dark:text-stone-50",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9  px-3",
        lg: "h-11  px-8",
        icon: "h-10 w-10",
        unset: "",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
